import ReportCollectInputsButton from "@/components/ReportCollectInputsButton";
import { SacredCard } from "@/components/ui/SacredPrimitives";
import type { PaidReportCTAProps, ReportPlanId } from "@/components/PaidReportCTA.types";

export default function ReportCollectInputsCTA({
  planId,
  priceInr,
  headline,
  subhead,
  language = "en",
  collectInputsHref,
  collectInputsLabel,
  collectInputsNote,
  analyticsContext,
}: {
  planId: ReportPlanId;
  priceInr: number;
  headline: string;
  subhead: string;
  language?: "en" | "hi";
  collectInputsHref: string;
  collectInputsLabel?: string;
  collectInputsNote?: string;
  analyticsContext?: PaidReportCTAProps["analyticsContext"];
}) {
  return (
    <SacredCard
      className="p-5 sm:p-6 text-center"
      style={{
        background: "linear-gradient(135deg, var(--color-surface-warm, #fff8e1) 0%, #fff 100%)",
        borderColor: "var(--color-accent-border)",
      }}
    >
      <p
        className="text-[11px] font-bold uppercase tracking-wider mb-2"
        style={{ color: "var(--ish-accent)", letterSpacing: "0.12em" }}
      >
        Premium Report
      </p>
      <h3
        className="text-[18px] sm:text-[20px] font-bold mb-2"
        style={{ color: "var(--ish-dark)", fontFamily: "PT Serif, Georgia, serif" }}
      >
        {headline}
      </h3>
      <p className="text-[13px] mb-4" style={{ color: "var(--ish-text-muted)" }}>
        {subhead}
      </p>
      <ReportTrustBlock language={language} headline={headline} />
      <ReportCollectInputsButton
        planId={planId}
        href={collectInputsHref}
        label={collectInputsLabel ?? collectInputsButtonLabel(language, priceInr)}
        analyticsContext={analyticsContext}
      />
      <p className="mt-3 text-[11px]" style={{ color: "var(--ish-text-faint)" }}>
        {collectInputsNote ?? footerCopy(language)}
      </p>
    </SacredCard>
  );
}

function ReportTrustBlock({ language, headline }: { language: "en" | "hi"; headline: string }) {
  const reportNoun = reportNounFromHeadline(headline, language);
  const bullets =
    language === "hi"
      ? [
          `Aapki poori ${reportNoun} reading`,
          "Turant PDF + isi page par reading",
          "Surakshit Razorpay checkout",
        ]
      : [
          `Your full ${reportNoun} reading`,
          "Instant PDF + on-page reading",
          "Secure Razorpay checkout",
        ];
  const reassurance =
    language === "hi"
      ? "Swiss Ephemeris se ganit, Lahiri ayanamsa - vahi vidhi jo paramparik panditji istemaal karte hain."
      : "Calculated with Swiss Ephemeris on the Lahiri ayanamsa - the same method a traditional Jyotishi follows.";
  return (
    <div className="mx-auto mb-4 max-w-sm text-left" style={{ pointerEvents: "none" }}>
      <ul className="space-y-1.5">
        {bullets.map((bullet) => (
          <li
            key={bullet}
            className="flex items-start gap-2 text-[12px]"
            style={{ color: "var(--ish-text-muted)" }}
          >
            <TrustCheckIcon />
            <span>{bullet}</span>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[11px] leading-snug" style={{ color: "var(--ish-text-faint)" }}>
        {reassurance}
      </p>
    </div>
  );
}

function TrustCheckIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="var(--ish-accent, var(--color-accent))"
      strokeWidth="2.5"
      className="mt-0.5 shrink-0"
      aria-hidden
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

function reportNounFromHeadline(headline: string, language: "en" | "hi"): string {
  const cleaned = headline
    .replace(/^(get|unlock|see|read|view|download|order)\s+/i, "")
    .replace(/^(your|the|my|aapki|apni|aapka)\s+/i, "")
    .replace(/[—–:].*$/, "")
    .trim();
  if (cleaned) return cleaned;
  return language === "hi" ? "Kundali" : "Kundali";
}

function collectInputsButtonLabel(language: "en" | "hi", priceInr: number): string {
  return language === "hi"
    ? `Janam details bharein - ₹${priceInr}`
    : `Enter birth details - ₹${priceInr}`;
}

function footerCopy(language: "en" | "hi"): string {
  return language === "hi"
    ? "Pehle birth details bharein. Chart ready hote hi checkout khulega."
    : "Enter birth details first. Checkout opens after the chart has the inputs needed for your report.";
}
